import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
          scrolled || mobileMenuOpen ? 'bg-background-50/95 backdrop-blur-md' : 'bg-transparent'
        }`}
      >
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 flex items-center justify-between h-16 md:h-18">
          {/* Left: Desktop nav + dropdown */}
          <div className="flex items-center gap-6" ref={dropdownRef}>
            <div className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`hidden md:flex items-center gap-1.5 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                  scrolled ? 'text-foreground-800 hover:text-foreground-950' : 'text-white/90 hover:text-white'
                }`}
              >
                <span>Categories</span>
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-background-50 rounded-lg overflow-hidden z-50 max-h-[420px] overflow-y-auto hidden md:block">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center justify-between px-4 py-3 text-sm text-foreground-800 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer border-b border-background-200 last:border-b-0"
                    >
                      <span className="flex items-center gap-2.5">
                        <span className="w-5 h-5 flex items-center justify-center">
                          <i className={`${cat.icon} text-base text-foreground-500`}></i>
                        </span>
                        {cat.name}
                      </span>
                      <i className="ri-arrow-right-s-line text-foreground-400 text-sm"></i>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/"
              className={`hidden md:block text-sm font-medium whitespace-nowrap transition-colors ${
                scrolled ? 'text-foreground-600 hover:text-foreground-900' : 'text-white/80 hover:text-white'
              }`}
            >
              Home
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`hidden md:block text-sm font-medium whitespace-nowrap transition-colors ${
                scrolled ? 'text-foreground-600 hover:text-foreground-900' : 'text-white/80 hover:text-white'
              }`}
            >
              Guides
            </a>
          </div>

          {/* Center: Logo */}
          <div className="absolute left-1/2 -translate-x-1/2">
            <Link to="/" className="flex items-center gap-1.5">
              <span className={`font-heading text-lg md:text-xl font-semibold tracking-tight whitespace-nowrap transition-colors ${
                scrolled || mobileMenuOpen ? 'text-foreground-900' : 'text-white'
              }`}>
                Hipobuy Spreadsheet
              </span>
              <span className="w-1.5 h-1.5 rounded-full bg-accent-500 flex-shrink-0"></span>
            </Link>
          </div>

          {/* Right: Desktop CTAs + Mobile Hamburger */}
          <div className="flex items-center gap-3 md:gap-4">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`hidden sm:block text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                scrolled || mobileMenuOpen ? 'text-foreground-700 hover:text-foreground-950' : 'text-white/80 hover:text-white'
              }`}
            >
              buy it now
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`hidden md:inline-flex px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all cursor-pointer ${
                scrolled || mobileMenuOpen
                  ? 'bg-primary-500 text-white hover:bg-primary-600'
                  : 'bg-white text-foreground-900 hover:bg-white/90'
              }`}
            >
              hipobuy sheet
            </a>

            {/* Hamburger button - mobile only */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`md:hidden w-10 h-10 flex items-center justify-center rounded-full transition-colors cursor-pointer ${
                scrolled || mobileMenuOpen
                  ? 'text-foreground-800 hover:bg-background-200'
                  : 'text-white hover:bg-white/10'
              }`}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <i className="ri-close-line text-2xl"></i>
              ) : (
                <i className="ri-menu-line text-2xl"></i>
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-foreground-900/50 backdrop-blur-sm" onClick={closeMobileMenu}></div>
          <div className="absolute top-0 right-0 w-[85%] max-w-[380px] h-full bg-background-50 overflow-y-auto shadow-2xl animate-slide-in-right">
            <div className="pt-20 pb-8 px-6">
              {/* CTA Buttons */}
              <div className="flex flex-col gap-3 mb-8">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={closeMobileMenu}
                  className="flex items-center justify-center gap-2 w-full px-5 py-3 bg-primary-500 text-white font-semibold text-sm rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  hipobuy sheet
                  <i className="ri-arrow-right-up-line text-lg"></i>
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={closeMobileMenu}
                  className="flex items-center justify-center gap-2 w-full px-5 py-3 bg-foreground-900 text-white font-semibold text-sm rounded-full hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
                >
                  buy it now
                  <i className="ri-arrow-right-up-line text-lg"></i>
                </a>
              </div>

              {/* Nav Links */}
              <div className="flex items-center gap-6 mb-6 pb-6 border-b border-background-200">
                <Link
                  to="/"
                  onClick={closeMobileMenu}
                  className="text-sm font-semibold text-foreground-800 hover:text-primary-600 transition-colors"
                >
                  Home
                </Link>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={closeMobileMenu}
                  className="text-sm font-medium text-foreground-600 hover:text-primary-600 transition-colors"
                >
                  Guides
                </a>
              </div>

              {/* Categories */}
              <h3 className="font-label text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3">
                Product Categories
              </h3>
              <div className="space-y-0.5">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={closeMobileMenu}
                    className="flex items-center justify-between px-4 py-3 rounded-lg text-sm text-foreground-800 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer"
                  >
                    <span className="flex items-center gap-3">
                      <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-200/70">
                        <i className={`${cat.icon} text-base text-foreground-500`}></i>
                      </span>
                      <span className="font-medium">{cat.name}</span>
                    </span>
                    <i className="ri-arrow-right-s-line text-foreground-400 text-sm"></i>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Slide-in animation style */}
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        .animate-slide-in-right {
          animation: slideInRight 0.3s ease-out;
        }
      `}</style>
    </>
  );
}